import { ref, watch } from 'vue'
import client from '@/composables/homebaseClient'

export default function useHomebaseDeployments(initialParams = {}) {
    const awaitingSignups = ref([])
    const deploymentTasks = ref([])
    const users = ref([])
    const loading = ref(false)
    const error = ref(null)

    const params = ref({
        assignedTo: '',
        status: 'assigned',
        type: 'deployment_task',
        limit: 100,
        sortBy: 'performedAt',
        sortDir: 'desc',
        ...initialParams
    })

    const toQuery = () => {
        const q = new URLSearchParams()
        if (params.value.assignedTo) q.set('assignedTo', params.value.assignedTo)
        if (params.value.status) q.set('status', params.value.status)
        if (params.value.type) q.set('type', params.value.type)
        if (params.value.limit) q.set('limit', String(params.value.limit))
        if (params.value.sortBy) q.set('sortBy', params.value.sortBy)
        if (params.value.sortDir) q.set('sortDir', params.value.sortDir)
        return q.toString() ? `?${q.toString()}` : ''
    }

    const fetchAwaitingSignups = async () => {
        loading.value = true
        error.value = null
        try {
            const res = await client.get('/signups/awaiting-deployment')
            awaitingSignups.value = Array.isArray(res.data) ? res.data : []
        } catch (err) {
            error.value = err.detail || 'Failed to load awaiting signups'
        } finally {
            loading.value = false
        }
    }

    const fetchDeploymentTasks = async () => {
        try {
            const res = await client.get(`/deployments${toQuery()}`)
            deploymentTasks.value = Array.isArray(res.data) ? res.data : []
        } catch (err) {
            console.error('Failed to load deployment tasks:', err)
        }
    }

    const fetchUsers = async () => {
        try {
            const res = await client.get('/users?limit=100')
            users.value = Array.isArray(res.data) ? res.data : []
        } catch (err) {
            console.error('Failed to load users:', err)
        }
    }

    const assignDeployment = async (householdId, assignedTo, notes = '') => {
        try {
            const res = await client.post('/deployments/assign', {
                householdId,
                assignedTo,
                notes
            })
            await fetchDeploymentTasks() // refresh tasks
            return res.data
        } catch (err) {
            throw new Error(err.detail || 'Failed to assign deployment')
        }
    }

    const reassignDeployment = async (deploymentId, assignedTo) => {
        try {
            const res = await client.patch(`/deployments/${deploymentId}/assign`, { assignedTo })
            await fetchDeploymentTasks() // refresh tasks
            return res.data
        } catch (err) {
            throw new Error(err.detail || 'Failed to reassign deployment')
        }
    }

    const updateTaskStatus = async (taskId, status) => {
        try {
            const res = await client.patch(`/deployments/${taskId}/status`, { status })
            await fetchDeploymentTasks() // refresh tasks
            return res.data
        } catch (err) {
            throw new Error(err.detail || 'Failed to update task status')
        }
    }

    const reassignTask = async (taskId, assignedTo) => {
        try {
            const res = await client.patch(`/deployments/${taskId}/assign`, { assignedTo })
            await fetchDeploymentTasks() // refresh tasks
            return res.data
        } catch (err) {
            throw new Error(err.detail || 'Failed to reassign task')
        }
    }

    // Auto-fetch when params change
    watch(params, fetchDeploymentTasks, { deep: true })

    return {
        awaitingSignups,
        deploymentTasks,
        users,
        loading,
        error,
        params,
        fetchAwaitingSignups,
        fetchDeploymentTasks,
        fetchUsers,
        assignDeployment,
        reassignDeployment,
        updateTaskStatus,
        reassignTask
    }
}
